import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { BestSupplyByTankActionButtons } from '@components/Reports/BestSupplyByTank/ActionButtons'
import { getColumnDefs } from '@components/Reports/BestSupplyByTank/ColumnDefs'
import { GraviGrid, Horizontal } from '@gravitate-js/excalibrr'
import { useApi } from '@gravitate-js/the-armory'
import { useUserSettings } from '@hooks/settings'
import { useQuery } from '@tanstack/react-query'
import api, { getAPIURL } from '@utils/api'
import { isDefined } from '@utils/general'
import moment from 'moment/moment'
import React, { useCallback, useMemo, useState } from 'react'

export function BestSupplyByTank() {
  const [selectedDate, setSelectedDate] = useState(new Date())
  const uApi = useApi({ baseURLOverride: getAPIURL() })

  const { formatCurrency } = useFormatter()

  const getOverviewQuery = (date) =>
    useQuery({
      queryKey: ['overviewQuery', date],
      queryFn: () => uApi.post('best_supply_record/overview', null, { queryParams: new URLSearchParams({ date }) }),
    })

  const { data, isPending } = getOverviewQuery(
    moment(selectedDate).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm:ss')
  )

  const { updateSetting } = useUserSettings()

  const onDayChange = (date) => {
    setSelectedDate(date)
  }

  const costDiffStyle = (params) => {
    if (params?.value < 0)
      return {
        color: 'var(--theme-success)',
      }
    if (params?.value > 0)
      return {
        color: 'var(--theme-error)',
      }
    return {}
  }

  const deltaCellRenderer = (params) => {
    const value = typeof params?.value === 'object' ? params?.value?.value : params?.value

    return (
      <Horizontal style={{ gap: '0.5rem', alignItems: 'center' }} justifyContent='end'>
        {isDefined(value) ? formatCurrency(value, true, 4) : 'N/A'}
        {value < 0 && <CaretDownOutlined color='var(--theme-success)' />}
        {value > 0 && <CaretUpOutlined color='var(--theme-error)' />}
      </Horizontal>
    )
  }

  const getRowId = useCallback((row) => row?.data?.key, [])
  const columnDefs = useMemo(
    () => getColumnDefs(formatCurrency, costDiffStyle, deltaCellRenderer),
    [formatCurrency, costDiffStyle, deltaCellRenderer]
  )
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
      groupDefaultExpanded: -1,
      groupDisplayType: 'multipleColumns',
      defaultGroupOrder: 'asc',
      autoGroupColumnDef: {
        headerClass: 'suppress-column-move',
        minWidth: 200,
        pinned: 'left',
      },
      suppressAggFuncInHeader: true,
    }),
    []
  )

  return (
    <GraviGrid
      controlBarProps={{
        title: 'Best Supply By Tank',
        actionButtons: (
          <BestSupplyByTankActionButtons
            excelOptions={[
              {
                title: 'Download Best Supply Report',
                downloadEP: () => api.downloadBestSupplyByTank(selectedDate),
                fileName: `${moment(selectedDate).format('YYYY-MM-DD')} Best Supply Summary.xlsx`,
              },
            ]}
            onDayChange={onDayChange}
            date={selectedDate}
          />
        ),
      }}
      agPropOverrides={agPropOverrides}
      columnDefs={columnDefs}
      storageKey='best_supply_by_tank'
      loading={isPending}
      rowData={data?.rows}
      shouldInsertCreated
      onGridConfigChanged={(config) => updateSetting('gridConfig::best_supply_by_tank', config)}
    />
  )
}
